import { createSelector } from 'reselect'
import { sort } from 'ramda'
import { RootState } from 'src/state/types'
import { sortByMainTextUnitNumber } from 'utils/array'
import { searchPlaceDetails } from './utils'
import {
  TGooglePlace,
  TGooglePlacePrediction,
  TLightstoneProperty,
  TMaybeId,
  TProperty,
  TPropertySearchResult,
  TPropertyState,
  TPropertySummary,
  TWithText,
} from './types'

/**
 * Moved out of `state.ts`, where they had been built onto a single `selectors: $TSFixMe`
 * object. The file they replace here was dead: `NAMESPACE` was `'api.property'` (the only API
 * module whose namespace carried the `api.` prefix), so its `getSlice` built `api.api.property.*`
 * paths that always resolved to undefined — and nothing imported it. `NAMESPACE` is now the bare
 * `'property'`.
 */

const state = (state: RootState): RootState => state

const propertyState = (state: RootState): TPropertyState => state.api.property

const byId = <T>(entities: Record<string, T>, id: TMaybeId): T | undefined => (id ? entities[id] : undefined)

const withText = <T extends { mainText: string }>(r: T): TWithText<T> => ({ ...r, text: r.mainText })

/**
 * Google places
 */
export const getGooglePlaces = createSelector(propertyState, s => s.googlePlaces)

export const getGooglePlaceById = createSelector(
  getGooglePlaces,
  places =>
    (id: TMaybeId): TGooglePlace | undefined =>
      byId(places, id),
)

/**
 * Properties
 */
export const getProperties = createSelector(propertyState, s => s.properties)

export const getPropertyById = createSelector(
  getProperties,
  properties =>
    (id: TMaybeId): TProperty | undefined =>
      byId(properties, id),
)

export const getLightstoneProperties = createSelector(propertyState, s => s.lightstoneProperties)

export const getLightstonePropertyById = createSelector(
  getLightstoneProperties,
  properties =>
    (id: TMaybeId): TLightstoneProperty | undefined =>
      byId(properties, id),
)

export const getPropertyFieldById = createSelector(
  state,
  state =>
    <K extends keyof TProperty>(id: TMaybeId, field: K): TProperty[K] | undefined =>
      getPropertyById(state)(id)?.[field],
)

export const getPropertyPlaceId = createSelector(
  state,
  state =>
    (id: TMaybeId): string | undefined =>
      getPropertyFieldById(state)(id, 'placeId'),
)

export const getPropertyLightstoneId = createSelector(
  state,
  state =>
    (id: TMaybeId): string | undefined =>
      getPropertyFieldById(state)(id, 'lightstoneId'),
)

/**
 * `null` when the property is not in the store yet; otherwise the component's `longName`, or ''.
 * `detailIndex` is passed through to {@link searchPlaceDetails} — `1` takes the last match.
 */
export const getPropertyAddressComponentValueByType = createSelector(
  state,
  state =>
    (id: TMaybeId, type: string, detailIndex?: number): string | null => {
      const property = getPropertyById(state)(id)
      return property ? searchPlaceDetails(property, type, detailIndex) : null
    },
)

const componentSelector = (type: string, detailIndex?: number) =>
  createSelector(
    state,
    state =>
      (id: TMaybeId): string | null =>
        getPropertyAddressComponentValueByType(state)(id, type, detailIndex),
  )

export const getBuildingNameById = componentSelector('BuildingName', 1)
export const getUnitNumberById = componentSelector('UnitNumber', 1)
export const getSchemeNumberById = componentSelector('SchemeNumber')
export const getSchemeNameById = componentSelector('SchemeName')
export const getPropertyStreetNumber = componentSelector('StreetNumber')
export const getPropertyStreetName = componentSelector('StreetName')
export const getPropertyStreetType = componentSelector('StreetType')
export const getPropertySublocality = componentSelector('Sublocality')
export const getPropertyCity = componentSelector('Locality')
export const getPropertyProvince = componentSelector('AdministrativeArea', 1)
export const getPropertyPostalCode = componentSelector('PostalCode')
export const getPropertyCountry = componentSelector('Country')

/**
 * '' until the property is loaded. It used to interpolate the four `null` components into
 * `'null null null, null'`, which rendered as-is and — being truthy — meant
 * `usePortfolioProperty`'s `!propertyStreetAddress` check never fetched the property.
 */
export const getPropertyStreetAddress = createSelector(state, state => (id: TMaybeId): string => {
  if (!getPropertyById(state)(id)) {
    return ''
  }
  const streetNumber = getPropertyStreetNumber(state)(id)
  const streetName = getPropertyStreetName(state)(id)
  const streetType = getPropertyStreetType(state)(id)
  const sublocality = getPropertySublocality(state)(id)
  return `${streetNumber} ${streetName} ${streetType}, ${sublocality}`
})

export const getPropertyBuildingAddress = createSelector(state, state => (id: TMaybeId): string => {
  const unitNumber = getUnitNumberById(state)(id)
  const buildingName = getBuildingNameById(state)(id)
  const streetName = getPropertyStreetName(state)(id)
  const streetType = getPropertyStreetType(state)(id)
  const sublocality = getPropertySublocality(state)(id)
  return unitNumber && buildingName ? `${unitNumber} ${buildingName}, ${streetName} ${streetType}, ${sublocality}` : ''
})

/**
 * Summaries
 */
export const getSummaries = createSelector(propertyState, s => s.summaries)

export const getSummaryById = createSelector(
  getSummaries,
  summaries =>
    (id: TMaybeId): TPropertySummary | undefined =>
      byId(summaries, id),
)

/**
 * Search
 */
export const getSearchResults = createSelector(propertyState, s => s.searchResults)

export const getSearchResultsByQuery = createSelector(
  getSearchResults,
  results =>
    (query: string): TWithText<TPropertySearchResult>[] =>
      (results.entities[query]?.properties ?? []).map(withText),
)

export const getCombinedSearchResultsByQuery = createSelector(
  getSearchResults,
  results =>
    (query: string): TWithText<TGooglePlacePrediction>[] =>
      (results.entities[query]?.places ?? []).map(withText),
)

/**
 * Whether a combined (Google) search has already come back for this query. `places` is only
 * written by `combinedSearch_success`, so its presence - even as an empty list - is the signal.
 * An empty list is otherwise indistinguishable from "never searched".
 */
export const hasCombinedSearchResultsForQuery = createSelector(
  getSearchResults,
  results =>
    (query: string): boolean =>
      Array.isArray(results.entities[query]?.places),
)

/**
 * Search hits sorted by `mainText` / unit number, with saved properties (those with an `id`)
 * ahead of Google / Lightstone hits.
 */
export const getSearchResultsSortedByMainTextUnitNumber = createSelector(
  getSearchResults,
  results =>
    (query: string): TWithText<TPropertySearchResult>[] => {
      const sortedResults = sort(sortByMainTextUnitNumber, (results.entities[query]?.properties ?? []).map(withText))
      return [...sortedResults.filter(r => r.id), ...sortedResults.filter(r => !r.id)]
    },
)
